import { Input, makeStyles } from "@fluentui/react-components";
import { useEffect, useState } from "react";
import IDimension, { IThreshold } from "../../models/admin/IDimension";
import { HOP_RANGE_COLORS } from "../../utils/common/Constants";
import { AmdAmber } from "../common/icons/amber/Amber";
import { AmdGreen } from "../common/icons/green/Green";
import { AmdRed } from "../common/icons/red/red";
import { MultiProgressBar } from "../common/multi-progress/MultiProgressBar";

export const ThresholdInput: React.FunctionComponent<IThresholdInputProps> = (
  props: IThresholdInputProps
) => {
  const [newDimensionDetails, setNewDimensionDetails] = useState<IDimension>();
  const classes = useStyles();

  useEffect(() => {
    setNewDimensionDetails({ ...props.selectedDimension });
  }, [props.selectedDimension, props.resetTrigger]);

  const isValidThresholdLimit = (val: number) => val >= 1 && val <= 99;

  const isValidMeasureValue = (val: number, isNegative = false) => {
    if (Number.isNaN(val)) return false;
    if (isNegative) {
      return val >= -999 && val <= 999;
    }
    return val >= 0 && val <= 999;
  };

  const updateThreshold = (
    type: string,
    key: keyof IThreshold,
    value: number
  ) => {
    if (!newDimensionDetails) return;

    const updated: IDimension = {
      ...newDimensionDetails,
      thresholds: newDimensionDetails.thresholds?.map((thr) =>
        thr.type === type ? { ...thr, [key]: value } : thr
      ),
    };

    setNewDimensionDetails(updated);
    props.onChangeThresholdValue(updated);
  };

  return (
    <>
      {newDimensionDetails?.thresholds?.map((t: IThreshold) => {
        return (
          <div
            key={t.type}
            data-testid={`${t.type}-threshold`}
            className={`${t.type}-threshold`}
          >
            <div className="mt-1 mb-2">
              <div className="mb-2 fs-20 fw-400 fc--gray900">{t.label}</div>
            </div>

            <div className="mt-1 mb-2">
              <p>
                {t.prefixDesc}
                {t.earlyBuffer !== null && t.earlyBuffer !== undefined && (
                  <Input
                    type="number"
                    className={classes.customInput}
                    value={t.earlyBuffer?.toString()}
                    data-testid={`${t.type}-early-buffer`}
                    onChange={(_e, target) => {
                      const value = parseInt(target.value);
                      if (!isValidMeasureValue(value, true)) return;
                      updateThreshold(t.type, "earlyBuffer", value);
                    }}
                    onWheel={(e) => e.stopPropagation()}
                  />
                )}

                {t.earlyBuffer !== null &&
                  t.earlyBuffer !== undefined &&
                  t.suffixDesc}

                {t.lateBuffer !== null && t.lateBuffer !== undefined && (
                  <>
                    <Input
                      type="number"
                      className={classes.customInput}
                      value={t.lateBuffer?.toString()}
                      data-testid={`${t.type}-late-buffer`}
                      onChange={(_e, target) => {
                        const value = parseInt(target.value);
                        if (!isValidMeasureValue(value)) return;
                        updateThreshold(t.type, "lateBuffer", value);
                      }}
                    />
                    {t.suffixDesc1}
                  </>
                )}
                {t.earlyBuffer == null && t.suffixDesc}
              </p>
            </div>

            <div className="mt-1 mb-2">
              <div className="mb-2 fs-20 fw-400 fc--gray900">
                {`${newDimensionDetails.label} thresholds`}
              </div>
            </div>
            <MultiProgressBar
              poor={HOP_RANGE_COLORS.poor}
              moderate={HOP_RANGE_COLORS.moderate}
              good={HOP_RANGE_COLORS.good}
              barDivisionValue1={t.moderate.min}
              barDivisionValue2={t.green.min}
              unit="%"
              format={["poor", "good"]}
            />
            <div className="d-flex jc-spacebtwn d-flex--wrap mt-1 mb-2">
              <div className="amd-container-vcenter mb-1">
                <span className="d-flex jc-center">
                  <AmdRed />
                </span>
                <span className="ml-1">Poor :</span>
                <span className="ml-1">Less than equal to</span>
                <span className="ml-1">
                  <Input
                    type="number"
                    className={classes.customInput}
                    value={t.poor.max.toString()}
                    data-testid={`${t.type}-amberToRed`}
                    onChange={(_e, target) => {
                      const val = parseInt(target.value);
                      if (isValidThresholdLimit(val) && val < t.moderate.max) {
                        const updated = {
                          ...newDimensionDetails,
                          thresholds: newDimensionDetails.thresholds.map(
                            (thr) =>
                              thr.type === t.type
                                ? {
                                    ...thr,
                                    poor: { ...thr.poor, max: val },
                                    moderate: { ...thr.moderate, min: val + 1 },
                                  }
                                : thr
                          ),
                        };
                        setNewDimensionDetails(updated);
                        props.onChangeThresholdValue(updated);
                      }
                    }}
                  />
                </span>
                <span className="ml-1">%</span>
              </div>

              <div className="amd-container-vcenter mb-1">
                <span className="d-flex jc-center">
                  <AmdAmber />
                </span>
                <span className="ml-1">Moderate :</span>
                <span className="ml-1">
                  <Input
                    type="number"
                    className={classes.customInput}
                    value={t.moderate.min.toString()}
                    disabled
                  />
                </span>
                <span className="ml-1">to</span>
                <span className="ml-1">
                  <Input
                    type="number"
                    className={classes.customInput}
                    value={t.moderate.max.toString()}
                    data-testid={`${t.type}-greenToAmber`}
                    onChange={(_e, target) => {
                      const val = parseInt(target.value);
                      if (isValidThresholdLimit(val) && val >= t.moderate.min) {
                        const updated = {
                          ...newDimensionDetails,
                          thresholds: newDimensionDetails.thresholds.map(
                            (thr) =>
                              thr.type === t.type
                                ? {
                                    ...thr,
                                    moderate: { ...thr.moderate, max: val },
                                    green: { ...thr.green, min: val + 1 },
                                  }
                                : thr
                          ),
                        };
                        setNewDimensionDetails(updated);
                        props.onChangeThresholdValue(updated);
                      }
                    }}
                  />
                </span>
                <span className="ml-1">%</span>
              </div>

              <div className="amd-container-vcenter mb-1">
                <span className="d-flex jc-center">
                  <AmdGreen />
                </span>
                <span className="ml-1">Good :</span>
                <span className="ml-1">Greater than equal to</span>
                <span className="ml-1">
                  <Input
                    type="number"
                    className={classes.customInput}
                    value={t.green.min.toString()}
                    disabled
                  />
                </span>
                <span className="ml-1">%</span>
              </div>
            </div>
          </div>
        );
      })}
    </>
  );
};

export interface IThresholdInputProps {
  selectedDimension: IDimension;
  onChangeThresholdValue: (updated: IDimension) => void;
  resetTrigger?: number;
}

const useStyles = makeStyles({
  customInput: {
    width: "50px",
    paddingLeft: "5px",
    "& input": { padding: "0" },
  },
});
